/** Metadatos del pipeline de tablas del chart builder (espejo de server/chartPipeline.js). */

export const CHART_FEED_OPTIONS = [
  {
    value: 'base',
    label: 'Serie SQL (filtros)',
    shortLabel: 'Serie SQL',
    description: 'Datos crudos del query con filtros y columnas del dataset.',
  },
  {
    value: 'episodes',
    label: 'Tabla de episodios',
    shortLabel: 'Episodios',
    description: 'Resultado del análisis de episodios (tiempo en estado).',
  },
  {
    value: 'zones',
    label: 'Tabla de zonas',
    shortLabel: 'Zonas',
    description: 'Resultado del análisis espacial (agrupación por proximidad).',
  },
];

export const DEFAULT_POST_PROCESS_ORDER = ['episodes', 'zones'];

export function normalizePostProcessOrder(order) {
  const raw = Array.isArray(order) ? order : DEFAULT_POST_PROCESS_ORDER;
  const out = [];
  for (const step of raw) {
    if ((step === 'episodes' || step === 'zones') && !out.includes(step)) out.push(step);
  }
  for (const step of DEFAULT_POST_PROCESS_ORDER) {
    if (!out.includes(step)) out.push(step);
  }
  return out.slice(0, 2);
}

/** Pasos activos del pipeline en el orden configurado (solo episodios/zonas habilitados). */
export function orderedPipelineSteps(postProcessOrder, episodesEnabled, zonesEnabled) {
  return normalizePostProcessOrder(postProcessOrder).filter(
    (step) => (step === 'episodes' && episodesEnabled) || (step === 'zones' && zonesEnabled)
  );
}

/** El gráfico se alimenta del último paso activo a la derecha; si no hay pasos, serie SQL. */
export function resolveChartFeedFromPipeline(postProcessOrder, episodesEnabled, zonesEnabled) {
  const steps = orderedPipelineSteps(postProcessOrder, episodesEnabled, zonesEnabled);
  if (!steps.length) return 'base';
  return steps[steps.length - 1];
}

/** Al cargar specs antiguas con chartFeed explícito, reordenar para que ese paso quede al final. */
export function migratePostProcessOrder(order, episodesEnabled, zonesEnabled, savedChartFeed) {
  const normalized = normalizePostProcessOrder(order);
  const steps = orderedPipelineSteps(normalized, episodesEnabled, zonesEnabled);
  if (steps.length < 2) return normalized;
  if (savedChartFeed !== 'episodes' && savedChartFeed !== 'zones') return normalized;
  if (steps[steps.length - 1] === savedChartFeed) return normalized;
  const other = steps.find((s) => s !== savedChartFeed);
  if (!other) return normalized;
  return normalizePostProcessOrder([other, savedChartFeed]);
}

export function chartFeedLabel(feed) {
  return CHART_FEED_OPTIONS.find((o) => o.value === feed)?.shortLabel || 'Serie SQL';
}

export function chartFeedDescription(feed) {
  return CHART_FEED_OPTIONS.find((o) => o.value === feed)?.description || '';
}

export function syncChartSourceFields(chartFeed, episodeAnalysis, spatialZoneAnalysis) {
  const nextEpisode = episodeAnalysis
    ? {
        ...episodeAnalysis,
        chartSource: chartFeed === 'episodes' ? 'episodes' : 'original',
      }
    : null;
  const nextZones = spatialZoneAnalysis
    ? {
        ...spatialZoneAnalysis,
        chartSource: chartFeed === 'zones' ? 'zones' : 'original',
      }
    : null;
  return { episodeAnalysis: nextEpisode, spatialZoneAnalysis: nextZones };
}

/** De qué tabla lee el paso de episodios antes de transformar. */
export function getEpisodeInputSource(postProcessOrder, zonesEnabled) {
  if (!zonesEnabled) {
    return { source: 'base', label: 'Serie SQL (filtros)', shortLabel: 'Serie SQL' };
  }
  const order = normalizePostProcessOrder(postProcessOrder);
  const epIdx = order.indexOf('episodes');
  const znIdx = order.indexOf('zones');
  if (epIdx >= 0 && znIdx >= 0 && epIdx > znIdx) {
    return { source: 'zones', label: 'Tabla de zonas (salida del paso anterior)', shortLabel: 'Zonas' };
  }
  return { source: 'base', label: 'Serie SQL (filtros)', shortLabel: 'Serie SQL' };
}

/** De qué tabla lee el paso de zonas (siempre la serie SQL con unnest). */
export function getZonesInputSource() {
  return { source: 'base', label: 'Serie SQL (detecciones unnest)', shortLabel: 'Serie SQL' };
}

export function pipelineStepInputLabel(step, postProcessOrder, episodesEnabled, zonesEnabled) {
  if (step === 'zones') return getZonesInputSource().shortLabel;
  if (step === 'episodes') {
    return getEpisodeInputSource(postProcessOrder, zonesEnabled).shortLabel;
  }
  return 'Serie SQL';
}
